// src/utils/cnpj.ts

import { cnpjConfig } from "./config";

const CNPJ_LENGTH = 14;
const BASE_LENGTH = 12;
const WEIGHTS_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const WEIGHTS_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

const getCharValue = (char: string): number => {
  // 1. Normaliza para maiúsculo para garantir consistência
  const upperCode = char.toUpperCase().charCodeAt(0);

  // 2. Validação para Números (0-9): ASCII 48 a 57
  if (upperCode >= 48 && upperCode <= 57) {
    return upperCode - 48; // Retorna 0 a 9
  }

  // 3. Validação para Letras (A-Z): ASCII 65 a 90
  // Conforme norma da RFB, subtraímos 48 para obter o valor de peso (A=17, B=18...)
  if (upperCode >= 65 && upperCode <= 90) {
    return upperCode - 48; // Retorna 17 a 42
  }

  // 4. Fallback de segurança
  throw new Error(`Caractere inválido no CNPJ: ${char}`);
};

const calculateDv = (base: string, weights: number[]): number => {
  const sum = base.split("").reduce((total, char, index) => {
    return total + getCharValue(char) * weights[index];
  }, 0);

  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
};

export const normalizeCnpj = (
  value = "",
  allowAlphanumeric = false
): string => {
  const pattern = allowAlphanumeric ? /[^a-zA-Z0-9]/g : /\D/g;
  return value.replace(pattern, "").toUpperCase().slice(0, CNPJ_LENGTH);
};

const formatNormalizedCnpj = (cnpj: string): string => {
  if (!cnpj) return "";
  if (cnpj.length <= 2) return cnpj;
  if (cnpj.length <= 5) return `${cnpj.slice(0, 2)}.${cnpj.slice(2)}`;
  if (cnpj.length <= 8) {
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5)}`;
  }
  if (cnpj.length <= 12) {
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(
      5,
      8
    )}/${cnpj.slice(8)}`;
  }

  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(
    5,
    8
  )}/${cnpj.slice(8, 12)}-${cnpj.slice(12, 14)}`;
};

export const maskCNPJAlfa = (
  value = "",
  allowAlphanumeric = cnpjConfig.allowAlphanumeric
): string => {
  return formatNormalizedCnpj(normalizeCnpj(value, allowAlphanumeric));
};

export const isValidCnpj = (value = "", allowAlphanumeric = false): boolean => {
  const cnpj = normalizeCnpj(value, allowAlphanumeric);

  if (cnpj.length !== CNPJ_LENGTH) return false;

  // Impede sextractCNPJequências repetidas
  if (/^(\w)\1+$/.test(cnpj)) return false;

  // Se a flag estiver off, rejeita letras explicitamente
  if (!allowAlphanumeric && /[A-Z]/.test(cnpj)) return false;

  const base = cnpj.slice(0, BASE_LENGTH);
  const dv1 = calculateDv(base, WEIGHTS_1);
  const dv2 = calculateDv(`${base}${dv1}`, WEIGHTS_2);

  return cnpj.endsWith(`${dv1}${dv2}`);
};

export const extractCNPJ = (value: string): string => {
  if (!value) return "";

  const lastPart = value.split("|").pop()?.trim() ?? "";

  const match =
    lastPart.match(
      /[A-Z0-9]{2,3}\.[A-Z0-9]{3}\.[A-Z0-9]{2,3}\/[A-Z0-9]{4}-[A-Z0-9]{2}/i
    )?.[0] ?? "";

  if (!match) return "";

  return match.replace(/[^A-Z0-9]/gi, "").toUpperCase();
};

export interface ValidationResult {
  valid: boolean;
  message: string;
}

export const validateDocument = (
  value: string,
  allowAlphanumeric = false
): ValidationResult => {
  if (!value) return { valid: false, message: "Informe um CNPJ ou CPF" };

  const cleaned = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

  if (cleaned.length < 11) {
    return { valid: false, message: "CNPJ ou CPF incompleto" };
  }

  // CPF: 11 dígitos numéricos
  if (cleaned.length === 11) {
    if (!/^\d+$/.test(cleaned)) {
      return { valid: false, message: "CPF deve conter apenas números" };
    }
    if (/^(\d)\1+$/.test(cleaned)) {
      return {
        valid: false,
        message: "CPF com todos os dígitos iguais não é válido",
      };
    }
    // Validação de DV do CPF (inline para não depender de utils/index)
    const cpf = cleaned;
    for (let t = 9; t < 11; t++) {
      let d = 0;
      for (let c = 0; c < t; c++) d += parseInt(cpf[c]) * (t + 1 - c);
      d = ((10 * d) % 11) % 10;
      if (parseInt(cpf[t]) !== d) {
        return {
          valid: false,
          message: "CPF inválido — dígitos verificadores incorretos",
        };
      }
    }
    return { valid: true, message: "" };
  }

  if (cleaned.length > 11 && cleaned.length < 14) {
    return {
      valid: false,
      message: "CNPJ incompleto — são necessários 14 caracteres",
    };
  }

  // CNPJ: 14 caracteres
  if (cleaned.length === 14) {
    if (/^(\w)\1+$/.test(cleaned)) {
      return {
        valid: false,
        message: "CNPJ com todos os caracteres iguais não é válido",
      };
    }
    if (!allowAlphanumeric && /[A-Z]/.test(cleaned)) {
      return { valid: false, message: "CNPJ alfanumérico não está habilitado" };
    }
    if (!isValidCnpj(cleaned, allowAlphanumeric)) {
      return {
        valid: false,
        message: "CNPJ inválido — dígitos verificadores incorretos",
      };
    }
    return { valid: true, message: "" };
  }

  return { valid: false, message: "Documento inválido" };
};

/**
 * Remove caracteres de formatação de um documento (CNPJ/CPF).
 * Respeita cnpjConfig: se alfanumérico desabilitado, remove letras também.
 */
export const cleanDocument = (value: string): string => {
  const pattern = cnpjConfig.allowAlphanumeric ? /[^a-zA-Z0-9]/g : /\D/g;
  return value.replace(pattern, "").toUpperCase();
};

function performCPFMask(cpf: string): string {
  cpf = cpf.replace(/\D/g, "");
  cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
  cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
  cpf = cpf.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  return cpf;
}

function maskCNPJProgressive(value: string): string {
  const pattern = cnpjConfig.allowAlphanumeric ? /[^a-zA-Z0-9]/g : /\D/g;
  const cleaned = value.replace(pattern, "").toUpperCase().slice(0, 14);

  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 2)}.${cleaned.slice(2)}`;
  if (cleaned.length <= 8)
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
  if (cleaned.length <= 12)
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(
      5,
      8
    )}/${cleaned.slice(8)}`;
  return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(
    5,
    8
  )}/${cleaned.slice(8, 12)}-${cleaned.slice(12, 14)}`;
}

export const formatCNPJOrCPF = (data: string): string => {
  const pattern = cnpjConfig.allowAlphanumeric ? /[^a-zA-Z0-9]/g : /\D/g;
  const cleaned = data.replace(pattern, "").toUpperCase();

  if (cleaned.length <= 11 && /^\d+$/.test(cleaned)) {
    return performCPFMask(cleaned);
  }

  return maskCNPJProgressive(cleaned);
};

export const isValidCNPJ = (cnpj: string): boolean => {
  return isValidCnpj(cnpj, cnpjConfig.allowAlphanumeric);
};
